import { Icon } from 'src/ui/Icon';

// Mirrors the app's SuggestionBubble (app/src/components/llchat/SuggestionBubble.tsx).
// The matchmaker denormalizes this card onto the reply message's
// options.suggestion. Display-only here in the dashboard.
export interface SuggestionCardData {
  suggestionId: string;
  candidate: {
    userId: string;
    name: string;
    age: number;
    role: string;
    location: string;
    photoUrl?: string;
  };
  compatibilityScore: number;
  reasonsTitle: string;
  reasons: string[];
  decision: 'pass' | 'like' | null;
}

const SuggestionCard = ({ data }: { data: SuggestionCardData }) => {
  const { candidate, reasons, reasonsTitle, compatibilityScore } = data;
  // Drop empty parts so a missing role/location doesn't leave an orphan " · ".
  const heroSub = [candidate.role, candidate.location]
    .filter(Boolean)
    .join(' · ');
  const firstName = candidate.name.split(' ')[0];
  const title = reasonsTitle || `Why ${firstName}`;

  // Open the candidate's profile in a new tab — display-only card otherwise.
  const openProfile = () =>
    window.open(`/user-profile/${candidate.userId}`, '_blank', 'noopener');

  return (
    <div
      className="mm-suggest"
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(e) => {
        if (e.key === 'Enter') openProfile();
      }}
      style={{ marginTop: 8 }}
    >
      {/* hero image / placeholder */}
      <div
        style={{
          position: 'relative',
          height: 200,
          background: 'var(--bg-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {candidate.photoUrl ? (
          <img
            src={candidate.photoUrl}
            alt={candidate.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              inset: 0,
            }}
          />
        ) : (
          <span style={{ fontSize: 64, opacity: 0.4 }}>📷</span>
        )}
        {compatibilityScore > 0 && (
          <span
            className="row gap-1"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              padding: '3px 8px',
              borderRadius: 99,
              backdropFilter: 'blur(4px)',
            }}
          >
            <Icon name="sparkles" size={12} />
            {compatibilityScore}%
          </span>
        )}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '26px 14px 11px',
            background:
              'linear-gradient(to top, rgba(0,0,0,0.74), rgba(0,0,0,0))',
          }}
        >
          <div
            style={{
              color: '#fff',
              fontWeight: 700,
              fontSize: 'var(--fs-lg)',
              letterSpacing: '-0.01em',
            }}
          >
            {firstName}, {candidate.age}
          </div>
          {heroSub && (
            <div
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 'var(--fs-xs)',
                marginTop: 1,
              }}
            >
              {heroSub}
            </div>
          )}
        </div>
      </div>

      {/* reasons */}
      <div style={{ padding: '12px 14px 14px' }}>
        <div className="row gap-2" style={{ marginBottom: 8 }}>
          <Icon
            name="sparkles"
            size={14}
            style={{ color: 'var(--accent-text)' }}
          />
          <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
            {title}
          </span>
        </div>
        {Array.isArray(reasons) && reasons.length > 0 ? (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
            }}
          >
            {reasons.map((r, i) => (
              <li
                key={i}
                className="row"
                style={{
                  gap: 9,
                  alignItems: 'flex-start',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.45,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    background: 'var(--accent-bar)',
                    flexShrink: 0,
                    marginTop: 6,
                  }}
                />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div
          className="row gap-1"
          style={{
            marginTop: 11,
            color: 'var(--accent-text)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 600,
          }}
        >
          View profile <Icon name="chevron-right" size={14} />
        </div>
      </div>
    </div>
  );
};

export default SuggestionCard;
